import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

// Industry benchmarks (external data)
const INDUSTRY_BENCHMARKS = {
  email: {
    label: '📧 Email Newsletter',
    metrics: [
      { name: 'Open Rate', value: 30, description: 'Industry average open rate' },
      { name: 'Click-Through Rate', value: 5, description: 'Industry average CTR' }
    ]
  },
  social: {
    label: '📱 Social Media',
    metrics: [
      { name: 'Engagement Rate', value: 3, description: 'Industry average engagement' }
    ]
  },
  website: {
    label: '🌐 Website',
    metrics: [
      { name: 'Conversion Rate', value: 5, description: 'Industry average conversion' }
    ]
  }
};

export default function ModuleAnalytics({ modules, role }) {
  const [stats, setStats] = useState(null);
  const [platformAverages, setPlatformAverages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(null);
  const [comparisonMetric, setComparisonMetric] = useState('completionRate');

  useEffect(() => {
    if (modules && modules.length > 0) {
      fetchStats();
      fetchPlatformAverages();
    } else {
      setLoading(false);
    }
  }, [modules]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const statsPromises = modules.map(async (module) => {
        const res = await fetch(`/api/education-modules/${module._id}/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          return { ...module, stats: data };
        }
        return { ...module, stats: null };
      });
      
      const modulesWithStats = await Promise.all(statsPromises);
      
      const totals = modulesWithStats.reduce((acc, m) => {
        if (m.stats) {
          acc.views += m.stats.views || 0;
          acc.starts += m.stats.starts || 0;
          acc.completions += m.stats.completions || 0;
          acc.registrations += m.stats.registrations || 0;
          acc.quizAttempts += m.stats.quizAttempts || 0;
        }
        return acc;
      }, { views: 0, starts: 0, completions: 0, registrations: 0, quizAttempts: 0 });
      
      setStats({
        modules: modulesWithStats,
        totals
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformAverages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/education-modules/platform-stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlatformAverages(data.averages);
      }
    } catch (error) {
      console.error('Error fetching platform averages:', error);
    }
  };

  const getComparison = (userValue, platformValue) => {
    if (!platformValue || platformValue === 0) return { status: 'unknown', diff: 0 };
    const diff = userValue - platformValue;
    if (diff > 10) return { status: 'excellent', diff };
    if (diff > 0) return { status: 'good', diff };
    if (diff > -10) return { status: 'average', diff };
    return { status: 'needs_improvement', diff };
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-blue-600';
      case 'average': return 'text-yellow-600';
      case 'needs_improvement': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-blue-100 text-blue-800';
      case 'average': return 'bg-yellow-100 text-yellow-800';
      case 'needs_improvement': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const generateRecommendations = (moduleStats) => {
    const recs = [];
    const avg = platformAverages;
    
    if (!avg || !moduleStats) return recs;
    
    const completionRate = moduleStats.views > 0 ? (moduleStats.completions / moduleStats.views) * 100 : 0;
    const viewToStart = moduleStats.views > 0 ? (moduleStats.starts / moduleStats.views) * 100 : 0;
    const registrationRate = moduleStats.views > 0 ? (moduleStats.registrations / moduleStats.views) * 100 : 0;
    
    if (completionRate < avg.completionRate * 0.8) {
      recs.push('📝 Add a quiz at the end to increase completion rates');
    }
    if (viewToStart < avg.viewToStart * 0.8) {
      recs.push('🎯 Improve your module title and description to attract more starters');
    }
    if (registrationRate < avg.registrationRate * 0.8) {
      recs.push('💡 Add a stronger call-to-action at completion to convert readers');
    }
    if (moduleStats.shareClicks > 0 && moduleStats.registrations < moduleStats.shareClicks * 0.1) {
      recs.push('📤 Offer a reward for sharing to increase conversion from shares');
    }
    if (moduleStats.quizAccuracy && moduleStats.quizAccuracy < (avg.quizScore || 50)) {
      recs.push('📚 Make quiz questions clearer to improve quiz scores');
    }
    
    if (recs.length === 0) {
      recs.push('🎉 Great job! Your module is performing above average. Keep it up!');
    }
    
    return recs;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading analytics...</p>
      </div>
    );
  }

  if (!stats || stats.modules.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <div className="text-4xl mb-4">📊</div>
        <h3 className="text-lg font-semibold mb-2">No Data Yet</h3>
        <p className="text-gray-500">
          Create modules and share them with your audience to see analytics here.
        </p>
      </div>
    );
  }

  const totalModules = stats.modules.length;
  const userCompletionRate = stats.totals.views > 0 
    ? Math.round((stats.totals.completions / stats.totals.views) * 100) 
    : 0;
  const userViewToStart = stats.totals.views > 0 
    ? Math.round((stats.totals.starts / stats.totals.views) * 100) 
    : 0;
  const userRegistrationRate = stats.totals.views > 0 
    ? Math.round((stats.totals.registrations / stats.totals.views) * 100) 
    : 0;
  const avgQuizScore = stats.totals.quizAttempts > 0 
    ? Math.round(stats.modules.reduce((sum, m) => sum + (m.stats?.quizAccuracy || 0), 0) / stats.modules.filter(m => m.stats?.quizAttempts > 0).length)
    : 0;

  const comparisons = {
    completionRate: { user: userCompletionRate, platform: platformAverages?.completionRate || 0 },
    viewToStart: { user: userViewToStart, platform: platformAverages?.viewToStart || 0 },
    registrationRate: { user: userRegistrationRate, platform: platformAverages?.registrationRate || 0 },
    quizScore: { user: avgQuizScore, platform: platformAverages?.quizScore || 0 },
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Views</div>
          <div className="text-2xl font-bold">{stats.totals.views}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Completions</div>
          <div className="text-2xl font-bold">{stats.totals.completions}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Completion Rate</div>
          <div className="text-2xl font-bold">{userCompletionRate}%</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Avg. Quiz Score</div>
          <div className="text-2xl font-bold">{avgQuizScore || 0}%</div>
        </div>
      </div>

      {/* Benchmarking & Comparison */}
      {platformAverages && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">📊 Your Performance vs Platform Average</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">You</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Platform Avg</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Difference</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Object.entries(comparisons).map(([key, data]) => {
                  const diff = data.user - data.platform;
                  const status = diff > 10 ? 'excellent' : diff > 0 ? 'good' : diff > -10 ? 'average' : 'needs_improvement';
                  const labels = {
                    completionRate: 'Completion Rate',
                    viewToStart: 'View-to-Start',
                    registrationRate: 'Registration Rate',
                    quizScore: 'Quiz Score'
                  };
                  return (
                    <tr key={key}>
                      <td className="px-4 py-2 text-sm">{labels[key] || key}</td>
                      <td className="px-4 py-2 text-right text-sm font-medium">{data.user}%</td>
                      <td className="px-4 py-2 text-right text-sm text-gray-500">{data.platform}%</td>
                      <td className={`px-4 py-2 text-right text-sm font-medium ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {diff >= 0 ? '+' : ''}{diff}%
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className={`text-xs px-2 py-1 rounded ${getStatusBadge(status)}`}>
                          {status === 'excellent' ? '🚀 Excellent' : 
                           status === 'good' ? '👍 Good' : 
                           status === 'average' ? '📊 Average' : 
                           '📈 Opportunity'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Industry Benchmarks */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">📈 Industry Benchmarks (External)</h3>
        <p className="text-sm text-gray-500 mb-4">
          How Our Pickup education modules compare to traditional marketing channels
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(INDUSTRY_BENCHMARKS).map(([key, benchmark]) => {
            const userComparison = key === 'email' ? userViewToStart : 
                                  key === 'social' ? userRegistrationRate * 2 : 
                                  userCompletionRate;
            const isBetter = userComparison > benchmark.metrics[0]?.value * 2;
            
            return (
              <div key={key} className="border rounded-lg p-4">
                <div className="text-lg">{benchmark.label}</div>
                {benchmark.metrics.map((metric, idx) => {
                  const compareValue = key === 'email' ? userViewToStart : 
                                      key === 'social' ? userRegistrationRate * 2 : 
                                      userCompletionRate;
                  return (
                    <div key={idx} className="mt-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{metric.name}</span>
                        <span className="font-medium">Industry: {metric.value}%</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-600">Our Pickup</span>
                        <span className={`font-medium ${compareValue > metric.value * 1.5 ? 'text-green-600' : 'text-blue-600'}`}>
                          {compareValue}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div 
                          className={`h-1.5 rounded-full ${compareValue > metric.value * 1.5 ? 'bg-green-600' : 'bg-blue-600'}`}
                          style={{ width: `${Math.min(compareValue, 100)}%` }}
                        />
                      </div>
                      {compareValue > metric.value * 1.5 && (
                        <span className="text-xs text-green-600 mt-1 block">✅ {Math.round(compareValue / metric.value * 100 - 100)}% better</span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Module List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">📋 Module Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Views</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Starts</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Completions</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Registrations</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {stats.modules.map((module) => {
                const rate = module.stats?.views > 0 
                  ? Math.round((module.stats.completions / module.stats.views) * 100) 
                  : 0;
                return (
                  <tr key={module._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{module.title}</div>
                      <div className="text-xs text-gray-500">
                        {module.screens?.length || 0} screens
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">{module.stats?.views || 0}</td>
                    <td className="px-6 py-4 text-right">{module.stats?.starts || 0}</td>
                    <td className="px-6 py-4 text-right">{module.stats?.completions || 0}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`text-sm font-medium ${rate > 50 ? 'text-green-600' : rate > 25 ? 'text-yellow-600' : 'text-gray-600'}`}>
                        {rate}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">{module.stats?.registrations || 0}</td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/learn/${module.slug}`}
                        target="_blank"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View
                      </Link>
                      <button
                        onClick={() => setSelectedModule(selectedModule === module._id ? null : module._id)}
                        className="ml-2 text-gray-500 hover:text-gray-700 text-sm"
                      >
                        {selectedModule === module._id ? 'Hide' : 'Details'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected Module Details + Recommendations */}
      {selectedModule && (
        <div className="bg-white rounded-lg shadow p-6">
          {stats.modules.filter(m => m._id === selectedModule).map((module) => {
            const recommendations = generateRecommendations(module.stats);
            const moduleRate = module.stats?.views > 0 
              ? Math.round((module.stats.completions / module.stats.views) * 100) 
              : 0;
            
            return (
              <div key={module._id}>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-lg">{module.title}</h4>
                  <span className={`text-sm px-3 py-1 rounded ${
                    moduleRate > 50 ? 'bg-green-100 text-green-800' :
                    moduleRate > 25 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {moduleRate}% completion rate
                  </span>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-gray-500">Views</div>
                    <div className="text-lg font-semibold">{module.stats?.views || 0}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Completions</div>
                    <div className="text-lg font-semibold">{module.stats?.completions || 0}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Quiz Accuracy</div>
                    <div className="text-lg font-semibold">{module.stats?.quizAccuracy || 0}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Created</div>
                    <div className="text-sm">{new Date(module.createdAt).toLocaleDateString()}</div>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="border-t pt-4">
                  <h5 className="font-medium mb-2">💡 Recommendations</h5>
                  <div className="space-y-1">
                    {recommendations.map((rec, idx) => (
                      <p key={idx} className="text-sm text-gray-700">• {rec}</p>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
